import { obtenerMensajeError } from "utils/apiErrors";
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, Card } from "@mui/material";
import {
  AddAPhoto as AddAPhotoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

// Componentes SUI
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import FileDialog from "components/FileDialog"; // Importar FileDialog para el modal

// API
import BucketApi from "api/bucketApi";
import ProcesosApi from "api/procesosApi";

const SociedadProceso = ({ sociedad, procesoId, identificador, setError, isNew = false }) => {
  const [archivoSociedad, setArchivoSociedad] = useState([]);
  const [documentoUrl, setDocumentoUrl] = useState("");
  const [hasDocumento, setHasDocumento] = useState(false);
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false); // Estado para el modal de PDF

  // const nombreNormalizadoUPPER = sociedad.nombre_completo
  //   .normalize("NFD") // Descompone letras acentuadas
  //   .replace(/[\u0300-\u036f]/g, "") // Elimina los signos diacríticos
  //   .replace(/ñ/g, "n") // Reemplaza ñ
  //   .replace(/Ñ/g, "N") // Reemplaza Ñ
  //   .replace(/\s+/g, "_") // Reemplaza espacios por guiones bajos
  //   .toUpperCase();

  // const nombreNormalizadoLOWER = sociedad.nombre_completo
  //   .normalize("NFD")
  //   .replace(/[\u0300-\u036f]/g, "")
  //   .replace(/ñ/g, "n")
  //   .replace(/Ñ/g, "N")
  //   .replace(/\s+/g, "_")
  //   .toLowerCase();

  const cargarArchivoSociedad = async () => {
    try {
      const anexoResponse = await ProcesosApi.verificarAnexoProceso(procesoId, {
        tipo_anexo: `SOCIEDAD_${sociedad.id}`,
        contains: true,
      });
      //console.log("response.data: " + JSON.stringify(response.data));
      if (anexoResponse.data.exists) {
        const anexoSociedad =
          anexoResponse.data.anexos.length > 0
            ? [
                {
                  id: anexoResponse.data.anexos[0].anexo_id,
                  nombre_completo: anexoResponse.data.anexos[0].nombre_anexo || "No tiene",
                  key_s3: anexoResponse.data.anexos[0].key_s3,
                  fecha_creacion: anexoResponse.data.anexos[0].fecha_creacion,
                  isNew: false,
                },
              ]
            : [];
        //alert("anexoSociedad: " + JSON.stringify(anexoSociedad));
        setArchivoSociedad(anexoSociedad);
      }
      // Verificar si existe el archivo en el Bucket
      const fileResponse = await BucketApi.checkSolicitudFileExists(
        getDocumentoFileName(),
        identificador
      );
      setHasDocumento(anexoResponse.data.exists && fileResponse.data.exists);
      if (anexoResponse.data.exists && fileResponse.data.exists) {
        //const urlResponse = await BucketApi.getSolicitudFileUrl(getDocumentoFileName(), identificador);
        const urlResponse = await BucketApi.getSolicitudFileUrlFresh(
          getDocumentoFileName(),
          identificador
        );
        setDocumentoUrl(urlResponse.data.file_url);
      }
    } catch (error) {
      console.error("Error cargando el Archivo de la Sociedad:", error);
      setError(obtenerMensajeError(error, "Error al cargar el Archivo de la Sociedad"));
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await cargarArchivoSociedad();
      } catch (error) {
        console.error("Error cargando datos:", error);
        setError(obtenerMensajeError(error, "Error al cargar los datos"));
      } finally {
        setIsLoading(false);
      }
    };
    if (procesoId) {
      fetchData();
    }
  }, [procesoId]);

  const getDocumentoFileName = () => {
    return `sociedad_${sociedad.id}.pdf`;
  };

  const verificarDocumento = async () => {
    if (!identificador) return;
    try {
      // Verificar si existe en anexos
      const anexoResponse = await ProcesosApi.verificarAnexoProceso(procesoId, {
        tipo_anexo: `SOCIEDAD_${sociedad.id}`,
        contains: true,
      });
      // Verificar si existe el archivo en el Bucket
      const fileResponse = await BucketApi.checkSolicitudFileExists(
        getDocumentoFileName(),
        identificador
      );
      setHasDocumento(anexoResponse.data.exists && fileResponse.data.exists);
      if (anexoResponse.data.exists && fileResponse.data.exists) {
        //const urlResponse = await BucketApi.getSolicitudFileUrl(getDocumentoFileName(), identificador);
        const urlResponse = await BucketApi.getSolicitudFileUrlFresh(
          getDocumentoFileName(),
          identificador
        );
        setDocumentoUrl(urlResponse.data.file_url);
      }
    } catch (error) {
      console.error("Error verificando el Archivo de la Sociedad:", error);
      setHasDocumento(false);
    }
  };

  useEffect(() => {
    if (!isNew) {
      verificarDocumento();
    }
  }, [identificador, sociedad.nombre_completo, isNew]);

  const handleDocumentoChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !identificador) return;
    setIsLoading(true);
    try {
      if (hasDocumento) {
        await BucketApi.deleteSolicitudFile(getDocumentoFileName(), identificador);
        await ProcesosApi.eliminarAnexoProceso(procesoId, archivoSociedad[0].id);
      }
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", getDocumentoFileName());
      formData.append("file_type", "solicitud");
      formData.append("proceso_id", identificador);
      formData.append("registrar_anexo", "true");
      formData.append("tipo_anexo", `SOCIEDAD_${sociedad.id}`);
      formData.append("nombre_anexo", sociedad.nombre_completo);
      formData.append("incluir_en_pdf", "True");
      const response = await BucketApi.uploadFile(formData);
      if (response.data?.file_url) {
        setHasDocumento(true);
        setDocumentoUrl(response.data.file_url);
        // Verificar que todo se registró correctamente
        await verificarDocumento();
      }
    } catch (error) {
      console.error("Error subiendo el Archivo de la Persona:", error);
      setError(`Error al subir el Archivo de la Persona de ${sociedad.nombre_completo}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!identificador || !window.confirm("¿Está seguro(a) de eliminar el Archivo de la Persona?"))
      return;
    setIsLoading(true);
    try {
      if (hasDocumento) {
        await BucketApi.deleteSolicitudFile(getDocumentoFileName(), identificador);
        await ProcesosApi.eliminarAnexoProceso(procesoId, archivoSociedad[0].id);
      }
      // Actualizar estado local
      setArchivoSociedad(null);
      setHasDocumento(false);
      setDocumentoUrl("");
    } catch (error) {
      console.error("Error eliminando el Archivo de la Persona:", error);
      setError(`Error al eliminar el Archivo de la Persona de ${sociedad.nombre_completo}`);
    } finally {
      setIsLoading(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const renderContent = () => {
    if (hasDocumento) {
      return (
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: 150,
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="body1" color="textSecondary" align="center">
            Doc. Cargado
          </Typography>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "4px",
              borderRadius: "4px",
            }}
          >
            <IconButton size="small" onClick={triggerFileInput} color="info">
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            color="primary"
            sx={{ cursor: "pointer", mt: 2 }}
            onClick={() => setShowPdfViewer(true)}
          >
            Ver Doc.
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        onClick={triggerFileInput}
        sx={{
          width: "100%",
          height: 150,
          border: "2px dashed #ccc",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          backgroundColor: "#f8f9fa",
          transition: "all 0.2s",
          position: "relative",
          "&:hover": {
            borderColor: "primary.main",
            backgroundColor: "#f0f0f0",
          },
        }}
      >
        <AddAPhotoIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
        <Typography color="textSecondary" align="center" sx={{ px: 2 }}>
          {isNew
            ? "¡Nueva Persona! Click para subir el archivo"
            : "Click para subir el archivo de " + sociedad.nombre_completo}
        </Typography>
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          Formato requerido: PDF
        </Typography>
        {isNew && (
          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              display: "flex",
              gap: 1,
            }}
          >
            <IconButton size="small" onClick={handleDelete} color="error">
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Card>
      <SuiBox p={2}>
        <SuiBox mb={2}>
          <SuiTypography variant="h6" fontWeight="medium">
            {sociedad.nombre_completo}
          </SuiTypography>
        </SuiBox>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {renderContent()}
        </Box>

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={handleDocumentoChange}
          style={{ display: "none" }}
        />

        {isLoading && (
          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography color="textSecondary">Procesando...</Typography>
          </Box>
        )}

        {/* Modal para visualizar el PDF */}
        <FileDialog
          open={showPdfViewer}
          onClose={() => setShowPdfViewer(false)}
          title={`Persona - ${sociedad.nombre_completo}`}
          content={
            <embed
              src={documentoUrl}
              type="application/pdf"
              width="100%"
              height="500px"
              style={{ border: "none" }}
            />
          }
        />
      </SuiBox>
    </Card>
  );
};

SociedadProceso.propTypes = {
  sociedad: PropTypes.shape({
    id: PropTypes.number,
    nombre_completo: PropTypes.string.isRequired,
    isNew: PropTypes.bool,
  }).isRequired,
  procesoId: PropTypes.number.isRequired,
  identificador: PropTypes.string,
  setError: PropTypes.func.isRequired,
  isNew: PropTypes.bool,
};

export default SociedadProceso;
